/**
 * PATH: packages/email-hub/src/feature/reader-header/command/ShortcutHelp.tsx
 * FILE_ID: EMAILHUB-READERHEADER-SHORTCUT-HELP-001
 * PURPOSE: Simple `?` overlay listing the reader header keyboard shortcuts.
 * ROLE IN ARCHITECTURE: Controlled presentational overlay (plan shortcut policy).
 * MAIN EXPORTS: ReaderShortcutHelp.
 * NON-RESPONSIBILITIES: Key handling lives in useReaderHeaderShortcuts.
 * NOTES FOR FUTURE AI: Keep this list in sync with READER_HEADER_SHORTCUTS only;
 *   do not hand-add rows for shortcuts other modules own.
 */
import { Keyboard, X } from 'lucide-react'

import { READER_HEADER_SHORTCUTS } from './useReaderHeaderShortcuts'

export function ReaderShortcutHelp({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) {
  if (!open) return null
  return (
    <>
      <button
        type="button"
        className="fixed inset-0 z-[80] cursor-default bg-black/30"
        aria-label="Close keyboard shortcuts"
        onClick={onClose}
      />
      <section
        role="dialog"
        aria-modal="true"
        aria-label="Keyboard shortcuts"
        className="fixed left-1/2 top-1/2 z-[81] w-[min(360px,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 rounded-xl border border-border bg-popover text-popover-foreground shadow-2xl"
        data-testid="reader-shortcut-help"
      >
        <header className="flex items-center justify-between border-b border-border px-3 py-2.5">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <Keyboard className="h-4 w-4" aria-hidden />
            Keyboard shortcuts
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-accent pointer-coarse:h-11 pointer-coarse:w-11"
            aria-label="Close keyboard shortcuts"
          >
            <X className="h-4 w-4" />
          </button>
        </header>
        <dl className="grid gap-1.5 p-3">
          {READER_HEADER_SHORTCUTS.map((shortcut) => (
            <div
              key={shortcut.keys}
              className="flex items-center justify-between gap-3"
            >
              <dt>
                <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 text-[11px] font-mono font-semibold text-foreground">
                  {shortcut.keys}
                </kbd>
              </dt>
              <dd className="text-right text-xs text-muted-foreground">
                {shortcut.description}
              </dd>
            </div>
          ))}
        </dl>
      </section>
    </>
  )
}
